import React, { useEffect, useCallback, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { X } from "lucide-react";
import CreditTable from "./credit/CreditTable";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import { EMPTY_CREDIT_FILTERS } from "bundles/Internal/components/credit/CreditTableFilters";
import DateTimeSelect from "./common/DateTimeSelect";
import ConfirmDialog from "./common/ConfirmDialog";
import AddCreditsDialog from "./common/AddCreditsDialog";

function buildSearchFilters(activeFilters) {
  const filters = {};
  let hasAny = false;

  if (activeFilters.status?.length) {
    filters.status = activeFilters.status;
    hasAny = true;
  }

  if (activeFilters.credit_type?.length) {
    filters.credit_type = activeFilters.credit_type;
    hasAny = true;
  }

  return hasAny ? filters : null;
}

const STATUS_OPTIONS = ["pending", "available", "activated", "expired"];

const CreditsIndexPage = () => {
  const [credits, setCredits] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_CREDIT_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  const [editCredit, setEditCredit] = useState(null);
  const [editForm, setEditForm] = useState({ status: "", expire_at: "" });
  const [actionLoading, setActionLoading] = useState(false);

  const [deleteCredit, setDeleteCredit] = useState(null);
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);

  const fetchCredits = useCallback((query = "", pageNum = 1, limit = 25, activeFilters = {}) => {
    const hasQuery = Boolean(String(query || "").trim());
    const searchFilters = buildSearchFilters(activeFilters);
    const hasFilters = searchFilters !== null;

    setLoading(true);
    sh.get("/internal/search/credits", {
      params: {
        q: hasQuery ? query : undefined,
        page: pageNum,
        limit,
        ...(hasFilters && { filters: JSON.stringify(searchFilters) }),
      },
    })
      .then((res) => {
        const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCredits(payload);
        if (res.data?.pagy) {
          setTotalCount(res.data.pagy.count || payload.length);
        } else {
          setTotalCount(payload.length);
        }
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch credits", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchCredits(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchCredits, searchQuery, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchCredits(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchCredits, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleOpenEdit = useCallback((credit) => {
    setEditCredit(credit);
    setEditForm({
      status: credit.status || "",
      expire_at: credit.expire_at ? credit.expire_at.slice(0, 10) : "",
    });
  }, []);

  const handleUpdate = async () => {
    if (!editCredit) return;
    setActionLoading(true);
    try {
      const response = await sh.patch(`/internal/credits/${editCredit.id}`, editForm);
      setEditCredit(null);
      setCredits((prev) =>
        prev.map((c) => (c.id === editCredit.id ? response.data : c))
      );
      enqueueSnackbar("Credit updated", { variant: "success" });
    } catch (err) {
      const msg = err?.response?.data?.errors?.[0] || "Failed to update credit";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddCredits = async (form) => {
    const { company_id, ...creditForm } = form;
    setActionLoading(true);
    try {
      await sh.post(`/internal/companies/${company_id}/add_credits`, creditForm);
      setAddCreditsOpen(false);
      fetchCredits(searchQuery, page, rowsPerPage, filters);
      enqueueSnackbar("Credits added", { variant: "success" });
    } catch (err) {
      const msg = err?.response?.data?.errors?.[0] || "Failed to add credits";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCredit) return;
    setActionLoading(true);
    try {
      await sh.delete(`/internal/credits/${deleteCredit.id}`);
      setDeleteCredit(null);
      setCredits((prev) => prev.filter((c) => c.id !== deleteCredit.id));
      setTotalCount((prev) => prev - 1);
      enqueueSnackbar("Credit deleted", { variant: "success" });
    } catch (err) {
      const msg = err?.response?.data?.errors?.[0] || "Failed to delete credit";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <CreditTable
        credits={credits}
        onQueryChange={(q) => setSearchQuery(q)}
        filters={filters}
        onFiltersChange={setFilters}
        onAddClick={() => setAddCreditsOpen(true)}
        onEdit={handleOpenEdit}
        onDelete={(c) => setDeleteCredit(c)}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
      />

      <AddCreditsDialog
        open={addCreditsOpen}
        onClose={() => setAddCreditsOpen(false)}
        onSubmit={handleAddCredits}
        loading={actionLoading}
      />

      <Dialog
        open={Boolean(editCredit)}
        onClose={() => setEditCredit(null)}
        PaperProps={{ sx: { borderRadius: "12px", width: 380, maxWidth: "100%", m: 2 } }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2, borderBottom: "1px solid hsl(140 8% 88%)" }}>
          <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
            Modify Credit
          </Typography>
          <IconButton size="small" onClick={() => setEditCredit(null)} sx={{ p: 0.5, borderRadius: "6px", color: "hsl(150 10% 46%)", "&:hover": { backgroundColor: "hsl(140 8% 90%)" } }}>
            <X size={16} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)", mb: 0.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
              Status
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={editForm.status}
              onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "6px",
                  fontSize: 14,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(150 30% 12%)",
                  backgroundColor: "hsl(140 10% 95%)",
                  "& fieldset": { borderColor: "hsl(140 8% 88%)" },
                  "&:hover fieldset": { borderColor: "hsl(140 8% 88%)" },
                  "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 2 },
                },
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: 14, textTransform: "capitalize" }}>{s}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)", mb: 0.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
              Expires at
            </Typography>
            <DateTimeSelect
              value={editForm.expire_at}
              onChange={(val) => setEditForm((f) => ({ ...f, expire_at: val }))}
              placeholder="Pick a date…"
              dateOnly
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ px: 2.5, pb: 2.5, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            disableRipple
            onClick={() => setEditCredit(null)}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 10% 46%)",
              borderRadius: "8px",
              height: 40,
              px: 2,
              "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
            }}
          >
            Cancel
          </Button>
          <Button
            disableRipple
            disableElevation
            variant="contained"
            onClick={handleUpdate}
            disabled={actionLoading}
            sx={{
              height: 40,
              borderRadius: "6px",
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              "&:hover": { backgroundColor: "hsl(150 38% 17%)", opacity: 0.9 },
              "&.Mui-disabled": { opacity: 0.5, backgroundColor: "hsl(150 38% 17%)", color: "hsl(80 97% 72%)" },
            }}
          >
            {actionLoading ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteCredit)}
        onClose={() => setDeleteCredit(null)}
        onConfirm={handleDelete}
        title="Delete credit?"
        description="This action cannot be undone."
        confirmLabel="Delete"
        destructive
      />
    </Box>
  );
};

export default CreditsIndexPage;
